// ============================================================
// プランID（USER_PLAN）
// ============================================================

/** @graph-connects none */
const PLAN_LABELS: Record<string, string> = {
  '1': 'Regular',
  '2': 'Lite',
  '4': 'Plus Size',
};

// ============================================================
// 支払い方法（PAYMENT_TYPE）
// ============================================================

/** @graph-connects none */
const PAYMENT_TYPE_LABELS: Record<string, string> = {
  '1': 'クレジットカード',
  '2': 'NP後払い',
  '3': 'ギフト',
  '4': '銀行振込',
  '5': 'なし',
  '6': '登録済みクレジットカード',
  '7': 'Amazon Pay',
};

// ============================================================
// オプションID（USER_OPTION）
// ============================================================

/** @graph-connects none */
const OPTION_LABELS: Record<string, string> = {
  '1': 'ダブルボックス',
  '2': 'アクセサリー',
  '3': '婚活',
  '4': 'ワンセレクト',
  '5': 'ブランドオプション',
  '6': 'プライム',
  '7': 'オケージョンオプション',
  '8': 'セルフセレクト',
};

// ============================================================
// 購入タイプ（BUY_TYPE）
// ============================================================

/** @graph-connects none */
const BUY_TYPE_LABELS: Record<string, string> = {
  '1': '過去アイテム',
  '2': '色違い',
  '3': '手元アイテム',
  '4': 'おすすめEC',
  '5': '過去アイテム色違い',
  '6': 'ECアイテム',
};

// ============================================================
// 都道府県コード（JIS X 0401）
// ============================================================

/** @graph-connects none */
const PREFECTURE_LABELS: Record<string, string> = {
  '1': '北海道',
  '2': '青森県',
  '3': '岩手県',
  '4': '宮城県',
  '5': '秋田県',
  '6': '山形県',
  '7': '福島県',
  '8': '茨城県',
  '9': '栃木県',
  '10': '群馬県',
  '11': '埼玉県',
  '12': '千葉県',
  '13': '東京都',
  '14': '神奈川県',
  '15': '新潟県',
  '16': '富山県',
  '17': '石川県',
  '18': '福井県',
  '19': '山梨県',
  '20': '長野県',
  '21': '岐阜県',
  '22': '静岡県',
  '23': '愛知県',
  '24': '三重県',
  '25': '滋賀県',
  '26': '京都府',
  '27': '大阪府',
  '28': '兵庫県',
  '29': '奈良県',
  '30': '和歌山県',
  '31': '鳥取県',
  '32': '島根県',
  '33': '岡山県',
  '34': '広島県',
  '35': '山口県',
  '36': '徳島県',
  '37': '香川県',
  '38': '愛媛県',
  '39': '高知県',
  '40': '福岡県',
  '41': '佐賀県',
  '42': '長崎県',
  '43': '熊本県',
  '44': '大分県',
  '45': '宮崎県',
  '46': '鹿児島県',
  '47': '沖縄県',
};

// ============================================================
// ディメンション名のラベル
// ============================================================

/** @graph-connects none */
const DIMENSION_NAME_LABELS: Record<string, string> = {
  // デバイス系
  device_category: 'デバイスカテゴリ',
  os_name: 'OS',
  browser_name: 'ブラウザ',
  device_type: 'デバイスタイプ',
  // ユーザーコンテキスト系
  membership_status: '会員ステータス',
  plan_id: 'プラン',
  rental_status: 'レンタルステータス',
  payment_type: '支払い方法',
  is_come_back: 'カムバック',
  is_waiting: 'ウェイティング',
  has_reserved_plan: 'プラン予約あり',
  prefecture: '都道府県',
  // 複合ディメンション
  'plan_id:rental_status': 'プラン×レンタルステータス',
  'plan_id:option_id': 'プラン×オプション',
  option_id: 'オプション',
  // 流入・行動系
  utm_source: '流入元',
  buy_type: '購入タイプ',
  source: 'ソース',
  page_source: 'ページソース',
  method: '方法',
  payment_method: '決済方法',
  login_method: 'ログイン方法',
  step_name: 'ステップ名',
  depth_percent: 'スクロール深度',
};

// ============================================================
// ディメンション値→ラベルのマッピングテーブル
// ============================================================

/** @graph-connects none */
const PAGE_SOURCE_LABELS: Record<string, string> = {
  home: 'ホーム',
  items_checkout: 'アイテム詳細（Native）',
  deliveries_rental_items: '配送中アイテム',
  past_items: '過去アイテム',
  favorites: 'お気に入り',
  wearing_history: '着用履歴',
  coordinate: 'コーディネート',
  album: 'アルバム',
  ec: 'EC',
  rental_item_detail: 'アイテム詳細',
  other: 'その他',
};

/** @graph-connects none */
const DIMENSION_VALUE_LABELS: Partial<Record<string, Record<string, string>>> = {
  plan_id: PLAN_LABELS,
  payment_type: PAYMENT_TYPE_LABELS,
  option_id: OPTION_LABELS,
  buy_type: BUY_TYPE_LABELS,
  prefecture: PREFECTURE_LABELS,
  page_source: PAGE_SOURCE_LABELS,
};

// ============================================================
// 公開関数
// ============================================================

/**
 * ディメンション値を表示用ラベルに変換する。
 * マッピングが存在しない場合は元の値をそのまま返す。
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ディメンション値コードを日本語の表示ラベルに変換する
 * @graph-connects none
 */
export function formatDimensionValue(dimensionName: string, value: string): string {
  // 複合ディメンション（plan_id:rental_status, plan_id:option_id）の場合
  if (dimensionName === 'plan_id:rental_status') {
    const [planId, ...rest] = value.split(':');
    const planLabel = PLAN_LABELS[planId] ?? planId;
    return `${planLabel}:${rest.join(':')}`;
  }
  if (dimensionName === 'plan_id:option_id') {
    const [planId, ...rest] = value.split(':');
    const planLabel = PLAN_LABELS[planId] ?? planId;
    const optionLabel = OPTION_LABELS[rest.join(':')] ?? rest.join(':');
    return `${planLabel}:${optionLabel}`;
  }

  const labelMap = DIMENSION_VALUE_LABELS[dimensionName];
  if (labelMap) {
    return labelMap[value] ?? value;
  }
  return value;
}

/**
 * ディメンション名を表示用ラベルに変換する。
 * マッピングが存在しない場合は元の値をそのまま返す。
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ディメンション名を日本語の表示ラベルに変換する
 * @graph-connects none
 */
export function formatDimensionName(dimensionName: string): string {
  return DIMENSION_NAME_LABELS[dimensionName] ?? dimensionName;
}
